/**
 * Progress API Client
 * -------------------
 * Handles communication with the Lambda API for progress persistence.
 * Uses 3-document split: childProgress (hot), childActivity (append-only), childState (cold)
 */

import { firebaseAuth } from './firebase.js';

const API_BASE = import.meta.env.VITE_API_BASE;

/**
 * Helper for fetch with error handling
 */
async function apiFetch(path, options = {}) {
  const user = firebaseAuth.currentUser;
  const token = user ? await user.getIdToken() : null;
  const url = `${API_BASE}${path}`;
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers
    },
    ...options
  });

  if (!response.ok) {
    const error = new Error(`API error: ${response.status} ${response.statusText}`);
    error.status = response.status;
    throw error;
  }

  return response.json();
}

export const progressApi = {
  // ─────────────────────────────────────────────────────────────
  // Progress (Hot Path) - Section-group accuracy tracking
  // ─────────────────────────────────────────────────────────────

  /**
   * Load section-group progress for a child
   * @param {string} childId
   * @returns {Promise<{progress: Object, version: number}>}
   */
  async getProgress(childId) {
    return apiFetch(`/progress/${encodeURIComponent(childId)}`);
  },

  /**
   * Update single section-group accuracy after answering a question
   * @param {string} childId
   * @param {string} sectionGroupId
   * @param {boolean} correct
   * @returns {Promise<{success: boolean, progress: Object}>}
   */
  async updateProgress(childId, sectionGroupId, correct) {
    return apiFetch(`/progress/${encodeURIComponent(childId)}/update`, {
      method: 'POST',
      body: JSON.stringify({ sectionGroupId, correct })
    });
  },

  /**
   * Batch update progress for multiple section groups in one API call
   * @param {string} childId
   * @param {Array<{sectionGroupId: string, correct: boolean}>} updates
   * @returns {Promise<{success: boolean, processed: number, progress: Object}>}
   */
  async updateProgressBatch(childId, updates) {
    return apiFetch(`/progress/${encodeURIComponent(childId)}/update`, {
      method: 'POST',
      body: JSON.stringify({ updates })
    });
  },

  // ─────────────────────────────────────────────────────────────
  // Activity Log (Append-Only)
  // ─────────────────────────────────────────────────────────────

  /**
   * Load activity log for a child
   * @param {string} childId
   * @returns {Promise<{activityLog: Array}>}
   */
  async getActivity(childId) {
    return apiFetch(`/activity/${encodeURIComponent(childId)}`);
  },

  /**
   * Add activity entry (lesson complete, revision, test, badge)
   * @param {string} childId
   * @param {Object} activity - { type, title, lessonId?, score?, ... }
   * @returns {Promise<{success: boolean}>}
   */
  async addActivity(childId, activity) {
    return apiFetch(`/activity/${encodeURIComponent(childId)}`, {
      method: 'POST',
      body: JSON.stringify(activity)
    });
  },

  // ─────────────────────────────────────────────────────────────
  // State (Cold) - Settings, preferences, continue state
  // ─────────────────────────────────────────────────────────────

  /**
   * Load state (settings, preferences, continue state, weekly targets)
   * @param {string} childId
   * @returns {Promise<{continueState, overrideLesson, preferences, dailyStreak, weeklyTargetsCore, version}>}
   */
  async getState(childId) {
    return apiFetch(`/state/${encodeURIComponent(childId)}`);
  },

  /**
   * Patch state with partial updates
   * @param {string} childId
   * @param {Object} updates - Partial state to merge
   * @returns {Promise<{success: boolean}>}
   */
  async patchState(childId, updates) {
    return apiFetch(`/state/${encodeURIComponent(childId)}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
  },

  /**
   * Seed baseline progress from a diagnostic assessment.
   * Seeds can only raise existing accuracy, never lower it.
   * @param {string} childId
   * @param {Object} seeds - { [sectionGroupId]: { accuracy, attempts, difficultyLevel } }
   * @returns {Promise<{success: boolean, applied: number, skipped: number}>}
   */
  async seedProgress(childId, seeds) {
    return apiFetch(`/progress/${encodeURIComponent(childId)}/seed`, {
      method: 'POST',
      body: JSON.stringify({ seeds })
    });
  },

  // ─────────────────────────────────────────────────────────────
  // Convenience: Load all 3 documents in parallel
  // ─────────────────────────────────────────────────────────────

  /**
   * Load all progress data for a child (parallel fetch)
   * @param {string} childId
   * @returns {Promise<{progress, activityLog, continueState, overrideLesson, preferences, dailyStreak, weeklyTargetsCore}>}
   */
  async loadAll(childId) {
    const [progressData, activityData, stateData] = await Promise.all([
      this.getProgress(childId).catch(err => {
        console.warn('Failed to load progress:', err);
        return { progress: {} };
      }),
      this.getActivity(childId).catch(err => {
        console.warn('Failed to load activity:', err);
        return { activityLog: [] };
      }),
      this.getState(childId).catch(err => {
        console.warn('Failed to load state:', err);
        return {};
      })
    ]);

    return {
      progress: progressData.progress || {},
      progressVersion: progressData.version || 1,
      activityLog: activityData.activityLog || [],
      continueState: stateData.continueState || null,
      overrideLesson: stateData.overrideLesson || null,
      preferences: stateData.preferences || {},
      dailyStreak: stateData.dailyStreak || { count: 0, lastActiveDate: null },
      weeklyTargetsCore: stateData.weeklyTargetsCore || [],
      revisionHistory: stateData.revisionHistory || [],
      xp: stateData.xp || null,
      stateVersion: stateData.version || 1
    };
  }
};

export default progressApi;
